import { cross3, sub3 } from './vector-math.js';
import { notRenderableError, triangleRenderability } from './validation.js';

/**
 * An edge's identity is the sorted pair of its endpoint vertex ids —
 * deterministic and derivable, so it needs no counter of its own (see the
 * module doc comment). Plain string sort is fine: the pair only needs a
 * consistent order, not a numeric one.
 *
 * **Injective for any accepted vertex id.** `addMesh`/`parseTopology` place
 * no restriction on vertex id *content*, so the obvious `[a, b].sort().join('_')`
 * is not a safe identity: `["a_b", "c"]` and `["a", "b_c"]` both join to
 * `"a_b_c"`, and every derived structure keyed by that string — most
 * consequentially `coplanarRegion`'s edge -> incident-faces adjacency index —
 * would then treat two disjoint, geometrically unrelated edges as one, walking
 * a "region" across a seam that does not exist. Two encodings therefore share
 * this one function: the readable `a_b` form when *both* ids are strictly
 * alphanumeric (no delimiter to be ambiguous about, so the single `_`
 * always splits the pair back exactly — and the case covering every id this
 * module itself mints, so `v1_v2` and every id already persisted or shown in
 * the edit-mode UI is byte-for-byte unchanged), and a `JSON.stringify`'d pair
 * otherwise, whose independently quoted and escaped elements always parse back
 * to exactly the two original ids. The two forms cannot collide with each
 * other either — the JSON form always starts with `[`, which the alphanumeric
 * form can never contain.
 *
 * Used for *both* roles an edge string plays here: the stable display/
 * selection identity handed out by `edgesOfTopology`, and the internal
 * adjacency/dedup key used by region discovery and subdivision. Those were
 * briefly split into two functions with different guarantees; one identity
 * that is correct for both is simpler and leaves no unsafe variant lying
 * around for the next caller to reach for.
 */
export function edgeId(vertexIdA, vertexIdB) {
  const pair = [vertexIdA, vertexIdB].sort();
  return ALPHANUMERIC_ID.test(pair[0]) && ALPHANUMERIC_ID.test(pair[1])
    ? `${pair[0]}_${pair[1]}`
    : JSON.stringify(pair);
}

// Deliberately excludes `_` itself: the readable `a_b` form above is only
// unambiguous when neither id can contribute an underscore of its own.
const ALPHANUMERIC_ID = /^[A-Za-z0-9]+$/;

/**
 * Return explicit edge metadata after replacing raw edges affected by a
 * topology mutation. Absence remains absence; present results are canonical.
 */
export function propagateExplicitEdgeIds(explicitEdgeIds, replacements) {
  if (explicitEdgeIds !== undefined && !Array.isArray(explicitEdgeIds)) {
    throw new Error('explicitEdgeIds must be an array when present');
  }
  if (!(replacements instanceof Map)) throw new Error('replacements must be a Map');

  for (const [removedEdgeId, replacementEdgeIds] of replacements) {
    if (typeof removedEdgeId !== 'string') throw new Error('replacement edge id must be a string');
    if (!Array.isArray(replacementEdgeIds)) {
      throw new Error(`replacement for '${removedEdgeId}' must be an array`);
    }
    replacementEdgeIds.forEach((id, index) => {
      if (typeof id !== 'string') {
        throw new Error(`replacement for '${removedEdgeId}'[${index}] must be a string`);
      }
    });
  }

  if (explicitEdgeIds === undefined) return undefined;

  const propagated = new Set();
  explicitEdgeIds.forEach((id, index) => {
    if (typeof id !== 'string') throw new Error(`explicitEdgeIds[${index}] must be a string`);
    const replacementEdgeIds = replacements.get(id);
    if (replacementEdgeIds === undefined) propagated.add(id);
    else for (const replacementEdgeId of replacementEdgeIds) propagated.add(replacementEdgeId);
  });
  return [...propagated].sort();
}

/**
 * Every unique edge implied by the topology's faces, deduplicated by
 * `edgeId`. There is no persisted edge list — this is derived fresh from
 * `faces` each time, the same way it would be if an edge-level command
 * existed; display/picking code should call this rather than inventing its
 * own edge-walking logic.
 */
export function edgesOfTopology(topology) {
  const seen = new Map();
  for (const face of topology.faces) {
    const { vertexIds } = face;
    for (let i = 0; i < vertexIds.length; i++) {
      const a = vertexIds[i];
      const b = vertexIds[(i + 1) % vertexIds.length];
      const id = edgeId(a, b);
      if (!seen.has(id)) seen.set(id, { id, vertexIds: [a, b] });
    }
  }
  return [...seen.values()];
}

export function findFace(topology, faceId) {
  const face = topology.faces.find((f) => f.id === faceId);
  if (!face) throw new Error(`unknown face id '${faceId}'`);
  return face;
}

export function positionsOfFace(topology, face) {
  const byId = new Map(topology.vertices.map((vertex) => [vertex.id, vertex]));
  return face.vertexIds.map((vertexId) => {
    const vertex = byId.get(vertexId);
    if (!vertex) throw new Error(`face '${face.id}' references unknown vertex '${vertexId}'`);
    return vertex.position;
  });
}

/**
 * Outward unit normal of a triangular face, via the cross product of its
 * first two edges — the same right-hand-rule convention three.js assumes for
 * front-face (CCW, as seen from outside) winding, which is what
 * `captureTopology`/`parseTopology` already preserve. There is no smoothed
 * per-vertex normal helper here on purpose: `buildGeometryFromTopology`'s
 * `computeVertexNormals()` is a *display* concern (smooth shading across
 * adjacent faces); this is the flat, single-face direction a topology-editing
 * operation like extrude needs to know which way to push new geometry.
 * Throws on an unknown face id or a degenerate (zero-area) triangle — there
 * is no reasonable normal for either.
 *
 * Degeneracy is the shared `triangleRenderability` rule — the same
 * scale-relative, strictly-positive-area test `flatFaceNormal` and
 * subdivision's own validation apply, replacing the bare absolute 1e-9 that
 * used to live here and that called every triangle in a millimetre-scale
 * model degenerate. Evaluated at float64 (`rounded: false`), because this is a
 * query about geometry the caller already has rather than a claim about what
 * will render; see `triangleRenderability`'s own comment for why that
 * distinction matters to region discovery.
 */
export function faceNormal(topology, faceId) {
  const face = findFace(topology, faceId);
  const corners = positionsOfFace(topology, face);
  const { renderable, reason } = triangleRenderability(corners, { rounded: false });
  if (!renderable) throw notRenderableError(faceId, reason);
  const [a, b, c] = corners;
  const normal = cross3(sub3(b, a), sub3(c, a));
  const length = Math.hypot(...normal);
  return normal.map((n) => n / length);
}
